import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export interface AdminSidebarBadgeProps {
  count: number;
  /** Tickets waiting for admin action (highlighted). */
  urgentCount?: number;
  collapsed?: boolean;
}

function formatCount(count: number): string {
  if (count > 99) return '99+';
  return String(count);
}

function buildAriaLabel(count: number, urgentCount: number): string {
  const base = `${count} open mock credit ticket${count === 1 ? '' : 's'}`;
  if (urgentCount > 0) {
    return `${base}, ${urgentCount} awaiting your review`;
  }
  if (count > 0) {
    return `${base}, waiting for user`;
  }
  return base;
}

const AdminSidebarBadge: React.FC<AdminSidebarBadgeProps> = ({
  count,
  urgentCount = 0,
  collapsed = false,
}) => {
  if (count <= 0) return null;

  const display = formatCount(count);
  const urgent = urgentCount > 0;
  const aria = buildAriaLabel(count, urgentCount);

  return (
    <AnimatePresence mode="wait">
      <motion.span
        key={display}
        className={[
          'admin-sidebar-badge',
          urgent ? 'admin-sidebar-badge--urgent' : 'admin-sidebar-badge--muted',
          collapsed ? 'admin-sidebar-badge--collapsed' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        title={aria}
        aria-label={aria}
        role="status"
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.6, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 500, damping: 28 }}
      >
        <span className="admin-sidebar-badge-value">{display}</span>
        {urgent && !collapsed ? (
          <span className="admin-sidebar-badge-dot" aria-hidden="true" />
        ) : null}
      </motion.span>
    </AnimatePresence>
  );
};

export default AdminSidebarBadge;
